import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import googleBusinessService from "./googleBusinessService";
import { GoogleBusinessState } from "../../types/googleBusiness";

const initialState: GoogleBusinessState = {
    accounts: [],
    locations: [],
    selectedAccount: null,
    selectedBusinesses: [],
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: "",
};

// ✅ **Fetch Google Business Accounts**
export const getGoogleAccounts = createAsyncThunk(
    "googleBusiness/getAccounts",
    async (token: string, thunkAPI) => {
        try {
            const response = await googleBusinessService.fetchGoogleAccounts(token);
            return response; // Ensure this returns { success, accounts: [] }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response?.data || "Failed to fetch accounts");
        }
    }
);

// ✅ **Fetch Locations (Businesses) under a Selected Account**
export const getGoogleLocations = createAsyncThunk(
    "googleBusiness/getLocations",
    async ({ accountId, token }: { accountId: string; token: string }, thunkAPI) => {
        try {
            const response = await googleBusinessService.fetchGoogleLocations(accountId, token);
            return response; // Ensure this returns { success, locations: [] }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response?.data || "Failed to fetch locations");
        }
    }
);

const googleBusinessSlice = createSlice({
    name: "googleBusiness",
    initialState,
    reducers: {
        reset: (state) => {
            state.isError = false;
            state.isSuccess = false;
            state.isLoading = false;
            state.message = "";
        },
        selectAccount: (state, action: PayloadAction<string>) => {
            state.selectedAccount = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // ✅ **Handle Fetching Google Business Accounts**
            .addCase(getGoogleAccounts.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getGoogleAccounts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.accounts = action.payload.accounts || [];
            })
            .addCase(getGoogleAccounts.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })

            // ✅ **Handle Fetching Locations Under Selected Account**
            .addCase(getGoogleLocations.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getGoogleLocations.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.locations = action.payload.businesses || [];
            })
            .addCase(getGoogleLocations.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            });
    },
});

export const { reset, selectAccount } = googleBusinessSlice.actions;
export default googleBusinessSlice.reducer;
